import { FC } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useMutation, useQueryClient } from 'react-query';

import { ApiErrorAlert } from 'components/ui/api-error-alert';
import { Button } from 'components/ui/button';
import { Input } from 'components/ui/input';
import { Label } from 'components/ui/label';
import { Textarea } from 'components/ui/textarea';
import { useService } from 'hooks/useService';
import { useToast } from 'hooks/useToast';
import { ApiError } from 'interfaces/api';
import { DatasetColumn } from 'interfaces/column';
import { IndicatorStub } from 'interfaces/indicator';
import { ContentType } from 'services/abstract';
import DatasetVariablePicker from './DatasetVariablePicker';
import EquationInput from './EquationInput';

type PageCartoIndicatorFormProps = {
  pageCartoId: number;
  columns: DatasetColumn[];
  onSubmit: (dataForm: IndicatorStub) => void;
};

export const PageCartoIndicatorForm: FC<PageCartoIndicatorFormProps> = ({
  pageCartoId,
  columns,
  onSubmit,
}) => {
  const queryClient = useQueryClient();
  const { addToast } = useToast();
  const indicatorService = useService(ContentType.INDICATOR);
  const {
    register,
    handleSubmit,
    formState: { errors, isValid },
    control,
    watch,
  } = useForm<IndicatorStub>();

  const formValues = watch();

  const { isError, error, isLoading, mutateAsync } = useMutation({
    mutationFn: async (indicator: IndicatorStub) => {
      return await indicatorService.addIndicatorToPageCarto(
        pageCartoId,
        indicator
      );
    },
    onSuccess: (_response, formData) => {
      addToast({
        title: 'Indicateur crée',
        description: `Votre indicateur a été crée avec succès`,
        type: 'success',
      });
      queryClient.invalidateQueries({ queryKey: ['page-carto', pageCartoId] });
      onSubmit(formData);
    },
  });

  const onSubmitForm = (data: IndicatorStub) => {
    mutateAsync(data);
  };

  return (
    <div
      className="p-4 bg-slate-100 border border-slate-300 rounded shadow-xl"
      data-testid="indicator-form"
    >
      <form onSubmit={handleSubmit(onSubmitForm)}>
        {isError && <ApiErrorAlert error={error as ApiError} />}
        <div className="flex flex-row">
          <div className="flex flex-col w-1/2 p-2">
            <div className="mt-3">
              <Label>1. Sélectionnez les colonnes à utiliser :</Label>
              <Controller
                name="variables"
                control={control}
                defaultValue={undefined}
                rules={{
                  required:
                    'Vous devez sélectionner les colonnes ainsi que le Géo Code.',
                  validate: (variables) => {
                    return variables.length > 0;
                  },
                }}
                render={({ field }) => {
                  return <DatasetVariablePicker columns={columns} {...field} />;
                }}
              />
            </div>
          </div>
          <div className="flex flex-col w-1/2 p-2">
            <div className="mt-3">
              <Label htmlFor="name">Nom</Label>
              <Input
                data-testid="name-input"
                id="name"
                className="w-full"
                {...register('name', {
                  required: `Veuillez saisir le nom de l'indicateur`,
                })}
              />
              {errors.name && (
                <div className="mt-2 text-xs text-red-600">
                  {errors.name.message}
                </div>
              )}
            </div>
            <div className="mt-3">
              <Label htmlFor="name">Description</Label>
              <Textarea
                data-testid="description-input"
                id="description"
                {...register('description', {
                  required: `Veuillez saisir la description`,
                })}
              />
              {errors.description && (
                <div className="mt-2 text-xs text-red-600">
                  {errors.description.message}
                </div>
              )}
            </div>
            <div className="mt-3">
              <Label>Equation</Label>
              <Controller
                name="equation"
                control={control}
                defaultValue={undefined}
                rules={{
                  required: 'Vous devez définir une formule de calcul.',
                }}
                render={({ field }) => {
                  return (
                    <EquationInput
                      variables={formValues.variables || []}
                      ref={field.ref}
                      value={field.value}
                      onChange={field.onChange}
                    />
                  );
                }}
              />
            </div>
            <div className="mt-3">
              <Label htmlFor="roundPrecision">
                Arrondi (nombre de décimales)
              </Label>
              <Input
                data-testid="round-precision-input"
                id="roundPrecision"
                type="number"
                step={1}
                min={0}
                placeholder="Laisser vide pour ne pas arrondir"
                {...register('roundPrecision', {
                  setValueAs: (value) => {
                    if (value === '' || value === null || value === undefined)
                      return undefined;
                    const parsed = Number(value);
                    return Number.isNaN(parsed) ? undefined : parsed;
                  },
                  validate: (value) => {
                    if (value === undefined) return true;
                    if (!Number.isInteger(value)) {
                      return 'Veuillez saisir un entier';
                    }
                    if (value < 0) {
                      return 'Veuillez saisir un entier supérieur ou égal à 0';
                    }
                    return true;
                  },
                })}
              />
              {errors.roundPrecision && (
                <div className="mt-2 text-xs text-red-600">
                  {errors.roundPrecision.message}
                </div>
              )}
            </div>
            <div className="mt-3">
              <Label htmlFor="origin">Source</Label>
              <Input
                data-testid="source-input"
                id="source"
                className="w-full"
                {...register('source', {
                  required: `Veuillez saisir la source`,
                })}
              />
              {errors.source && (
                <div className="mt-2 text-xs text-red-600">
                  {errors.source.message}
                </div>
              )}
            </div>
            <div className="mt-3">
              <Label htmlFor="origin">Validité</Label>
              <Input
                data-testid="validity-input"
                id="validity"
                className="w-full"
                {...register('validity', {
                  required: `Veuillez saisir la validité`,
                })}
              />
              {errors.validity && (
                <div className="mt-2 text-xs text-red-600">
                  {errors.validity.message}
                </div>
              )}
            </div>
            <div className="flex justify-end mt-3">
              <Button
                data-testid="submit-button"
                type="submit"
                size="lg"
                isLoading={isLoading}
                disabled={!isValid}
              >
                Valider
              </Button>
            </div>
          </div>
        </div>
      </form>
    </div>
  );
};
